import React, { useState, useEffect } from "react";

import { Tabs } from "antd";
import { format } from "date-fns";
import _ from "lodash";

import { useLumenV2Mutations } from "manager/hooks/api";
import RadioButtons from "shared/components/RadioButtons";
import { useConfirmation } from "shared/hooks/useConfirmation";
import { FormulaEditor } from "shared/components/vega/ExpressionEngine";

import CalculationSettings from "./CalculationSettings";
import Outcomes from "./Outcomes";
import ScoreLabels from "./ScoreLabels";

const LumenEditor = ({ formula, canEdit = false, selectedVersion = null }) => {
  const [resolvedFormula, setFormula] = useState(formula);

  // If viewing a historical version, use its data
  const [expressionFormula, setExpressionFormula] = useState(
    selectedVersion?.formula || resolvedFormula?.expression?.formula
  );
  const [calculationSettings, setCalculationSettings] = useState(
    selectedVersion?.settings
      ? _.omit(selectedVersion.settings, ["scoreLabels", "outcomes"])
      : _.omit(resolvedFormula?.settings, ["scoreLabels", "outcomes"])
  );
  const [scoreLabels, setScoreLabels] = useState(
    selectedVersion?.settings?.scoreLabels ||
      resolvedFormula?.settings?.scoreLabels
  );
  const [outcomes, setOutcomes] = useState(
    selectedVersion?.settings?.outcomes || resolvedFormula?.settings?.outcomes
  );

  const { updateLumenV2Formula, deleteLumenV2Formula } = useLumenV2Mutations();
  const { confirm } = useConfirmation();

  // Update state when selectedVersion changes
  useEffect(() => {
    if (selectedVersion) {
      // Use the expression data from the selected version
      if (selectedVersion.expression?.formula) {
        setExpressionFormula(selectedVersion.expression.formula);
      } else {
        // Fallback if expression data isn't available
        setExpressionFormula(
          `// Version from ${new Date(
            parseInt(selectedVersion.timestamp, 10)
          ).toLocaleString()} - Expression data not available`
        );
      }
      if (selectedVersion.settings) {
        setCalculationSettings(
          _.omit(selectedVersion.settings, ["scoreLabels", "outcomes"])
        );
        setScoreLabels(selectedVersion.settings.scoreLabels);
        setOutcomes(selectedVersion.settings.outcomes);
      }
    } else {
      // Reset to current version
      setExpressionFormula(resolvedFormula?.expression?.formula);
      setCalculationSettings(
        _.omit(resolvedFormula?.settings, ["scoreLabels", "outcomes"])
      );
      setScoreLabels(resolvedFormula?.settings?.scoreLabels);
      setOutcomes(resolvedFormula?.settings?.outcomes);
    }
  }, [selectedVersion, resolvedFormula]);

  const canSave =
    canEdit &&
    !selectedVersion &&
    _.some([
      resolvedFormula?.expression?.formula !== expressionFormula,
      !_.isEqual(
        calculationSettings,
        _.omit(resolvedFormula?.settings, ["scoreLabels", "outcomes"])
      ),
      !_.isEqual(scoreLabels, resolvedFormula?.settings?.scoreLabels),
      !_.isEqual(outcomes, resolvedFormula?.settings?.outcomes),
    ]);

  const applyReset = () => {
    setExpressionFormula(resolvedFormula?.expression?.formula);
    setCalculationSettings(
      _.omit(resolvedFormula?.settings, ["scoreLabels", "outcomes"])
    );
    setScoreLabels(resolvedFormula?.settings?.scoreLabels);
    setOutcomes(resolvedFormula?.settings?.outcomes);
  };

  const onReset = () => {
    if (canSave) {
      confirm(
        {
          title: 'Discard changes?',
          message: 'Your unsaved changes will be lost.',
          submitButtonLabel: 'Discard changes',
          cancelLinkLabel: 'Keep editing',
        },
        applyReset
      );
    } else {
      applyReset();
    }
  };

  const onSave = async () => {
    setFormula(
      await updateLumenV2Formula({
        id: resolvedFormula?.id,
        data: {
          formula: expressionFormula,
          settings: {
            ...calculationSettings,
            scoreLabels,
            outcomes,
          },
        },
      })
    );
  };

  const onDelete = () => {
    confirm(
      {
        title: 'Delete formula?',
        message: 'This formula will be permanently deleted. This action cannot be undone.',
        submitButtonLabel: 'Delete formula',
        cancelLinkLabel: 'Cancel',
      },
      () => deleteLumenV2Formula(resolvedFormula?.id)
    );
  };

  return (
    <div className="vega flex no-wrap no-gap lumen workflow-editor">
      <FormulaEditor
        formula={expressionFormula}
        canSave={canSave}
        canEdit={canEdit && !selectedVersion}
        onChange={setExpressionFormula}
        onReset={onReset}
        onSave={onSave}
        onDelete={onDelete}
        versionNotice={
          selectedVersion && (
            <div className="vega version-notice">
              Viewing version from{" "}
              {selectedVersion.timestamp
                ? format(
                    new Date(parseInt(selectedVersion.timestamp, 10)),
                    "MMM dd, yyyy h:mm a"
                  )
                : "unknown time"}
            </div>
          )
        }
      />

      {_.startsWith(resolvedFormula?.identifier, "application") && (
        <div className="vega content flex column settings">
          <Tabs defaultActiveKey="calculation-settings" animated={false}>
            <Tabs.TabPane key="calculation-settings" tab="Calculation Settings">
              <CalculationSettings
                settings={_.cloneDeep(calculationSettings)}
                canEdit={canEdit && !selectedVersion}
                onChange={setCalculationSettings}
              />
            </Tabs.TabPane>

            <Tabs.TabPane key="score-labels" tab="Score Labels">
              <ScoreLabels
                scoreLabels={_.cloneDeep(scoreLabels || {})}
                canEdit={canEdit && !selectedVersion}
                onChange={setScoreLabels}
              />
            </Tabs.TabPane>

            <Tabs.TabPane key="outcomes" tab="Outcomes">
              <Outcomes
                outcomes={_.cloneDeep(outcomes || {})}
                canEdit={canEdit && !selectedVersion}
                onChange={setOutcomes}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
      )}

      {(_.includes(resolvedFormula?.identifier, "applicant") ||
        _.includes(resolvedFormula?.identifier, "guarantor")) && (
        <div className="vega content flex column settings">
          <div className="vega flex centered">
            <span className="vega label larger">Passthrough Outcomes:</span>
            <RadioButtons
              menuItems={[
                { key: "enabled", name: "Enabled" },
                { key: "disabled", name: "Disabled" },
              ]}
              activeItem={
                calculationSettings?.passthroughOutcomes
                  ? "enabled"
                  : "disabled"
              }
              onChange={(value) =>
                setCalculationSettings({
                  ...calculationSettings,
                  passthroughOutcomes: value === "enabled",
                })
              }
              disabled={!canEdit || !!selectedVersion}
              theme="gray"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LumenEditor;
